import React, { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  ChevronRightIcon,
  HomeIcon,
  BuildingStorefrontIcon,
  ArchiveBoxIcon,
  WrenchScrewdriverIcon,
  UserCircleIcon,
  TruckIcon,
  DocumentTextIcon,
  PuzzlePieceIcon,
} from '@heroicons/react/24/outline'
import { BreadcrumbItem } from '../../../types/layout'

interface BreadcrumbsProps {
  items: BreadcrumbItem[]
}

/**
 * Miga de pan: orientación discreta sobre el título de página.
 */
const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
  const location = useLocation()

  const RouteIcon = useMemo(() => {
    const path = location.pathname.toLowerCase()
    if (path.includes('orders')) return BuildingStorefrontIcon
    if (path.includes('products')) return ArchiveBoxIcon
    if (path.includes('settings')) return WrenchScrewdriverIcon
    if (path.includes('profile')) return UserCircleIcon
    if (path.includes('envios')) return TruckIcon
    if (path.includes('cotizaciones')) return DocumentTextIcon
    if (path.includes('erp')) return PuzzlePieceIcon
    return null
  }, [location.pathname])

  if (items.length === 0) return null

  return (
    <nav
      className="flex items-center gap-1 text-[11px] leading-none text-gray-500"
      aria-label="Breadcrumb"
    >
      <Link
        to="/home"
        className="inline-flex items-center gap-1 rounded text-gray-500
                   hover:text-custom-blue transition-colors duration-150"
        aria-label="Ir a inicio"
      >
        <HomeIcon className="h-3.5 w-3.5 shrink-0" />
        <span className="hidden sm:inline">Inicio</span>
      </Link>

      {items.map((crumb, index) => {
        const isLast = crumb.isLast

        return (
          <React.Fragment key={`${crumb.to}-${index}`}>
            <ChevronRightIcon
              className="h-3 w-3 shrink-0 text-gray-300"
              aria-hidden="true"
            />

            {isLast ? (
              <span
                className="inline-flex min-w-0 items-center gap-1 font-medium text-gray-700"
                aria-current="page"
              >
                {RouteIcon && (
                  <RouteIcon className="h-3.5 w-3.5 shrink-0 text-custom-blue" />
                )}
                <span className="truncate max-w-[140px] sm:max-w-[220px] md:max-w-none">
                  {crumb.label}
                </span>
              </span>
            ) : (
              <Link
                to={crumb.to}
                className="truncate max-w-[100px] sm:max-w-[160px] text-gray-500
                           hover:text-custom-blue transition-colors duration-150"
              >
                {crumb.label}
              </Link>
            )}
          </React.Fragment>
        )
      })}
    </nav>
  )
}

export default React.memo(Breadcrumbs)
